import { useQuery } from '@tanstack/react-query';
import apiClient from '@lift-admin/utils/apiClient';
import { REPORT_CONFIG_BASE_URL, API_ENDPOINTS } from '../api';
import type { AllowableValue } from '../types';

interface AllowableValuesResponse {
  success: boolean;
  data: AllowableValue[];
}

interface BrandPlatformsResponse {
  success: boolean;
  data: string[];
}

export const useAllowableValues = () => {
  return useQuery<AllowableValue[], Error>({
    queryKey: ['report-configuration', 'allowable-values'],
    queryFn: async () => {
      const { data } = await apiClient.get<AllowableValuesResponse>(
        `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.ALLOWABLE_VALUES}`
      );
      return data.data || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useBrandPlatforms = (brandId: number | null) => {
  return useQuery<string[], Error>({
    queryKey: ['report-configuration', 'brand-platforms', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data } = await apiClient.get<BrandPlatformsResponse>(
        `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.BRAND_PLATFORMS(brandId)}`
      );
      return data.data || [];
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useValidateSelection = (
  platform: string | null,
  reportType: string | null,
  granularityLevel: string | null,
  cadence: string | null,
  metricType: string | null
) => {
  const params = new URLSearchParams();
  if (platform) params.append('platform', platform);
  if (reportType) params.append('report_type', reportType);
  if (granularityLevel) params.append('granularity_level', granularityLevel);
  if (cadence) params.append('cadence', cadence);
  if (metricType) params.append('metric_type', metricType);

  return useQuery({
    queryKey: ['report-configuration', 'validate', { platform, reportType, granularityLevel, cadence, metricType }],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `${REPORT_CONFIG_BASE_URL}${API_ENDPOINTS.VALIDATE_SELECTION}?${params.toString()}`
      );
      return data;
    },
    enabled: !!(platform && reportType),
    staleTime: 30 * 1000,
  });
};
